/* eslint-disable tailwindcss/no-custom-classname -- Using absolute values to set grid */
import {FC} from 'react'
import {Content} from '@prismicio/client'
import {PrismicRichText, SliceComponentProps} from '@prismicio/react'
import {EVENTS, getButtonsData, track} from 'src/common/helpers'
import {Context} from 'src/common/types'
import {useLinkResolver} from 'prismicio'
import {Icon} from 'src/common/Icon'
import Link from 'next/link'
import cn from 'classnames'
import {Gradient, GridCell} from './components'

const GRID_CLASSES: Record<number, string> = {
  2: 'grid-cols-[20px_1fr_1fr_20px] lg:grid-cols-[minmax(20px,1fr)_minmax(0,min(50%,610px))_minmax(0,min(25%,297px))_minmax(0,min(25%,297px))_minmax(20px,1fr)] md:max-lg:md:grid-cols-[minmax(40px,1fr)_minmax(0,min(50%,610px))_minmax(0,min(25%,297px))_minmax(0,min(25%,297px))_minmax(40px,1fr)]',
  3: 'grid-cols-[20px_1fr_1fr_1fr_20px] lg:grid-cols-[minmax(20px,1fr)_minmax(0,min(50%,610px))_minmax(0,min(16.667%,198.34px))_minmax(0,min(16.667%,198.34px))_minmax(0,min(16.667%,198.34px))_minmax(20px,1fr)] md:max-lg:grid-cols-[minmax(40px,1fr)_minmax(0,min(50%,610px))_minmax(0,min(16.667%,198.34px))_minmax(0,min(16.667%,198.34px))_minmax(0,min(16.667%,198.34px))_minmax(40px,1fr)]',
}

/**
 * Props for `SideGrid`.
 */
export type SideGridProps = SliceComponentProps<Content.SideGridSlice> & {context: Context}

/**
 * Component for "SideGrid" Slices.
 */
const SideGrid: FC<SideGridProps> = ({slice, context, index: componentIndex}) => {
  const {primaryButtonData} = getButtonsData({
    primaryButton: slice.primary.primary_button,
    buttonsData: context.buttonsData,
  })

  const {linkResolver} = useLinkResolver()

  return (
    <section className="flex w-full flex-col bg-moss-white pt-20 max-md:gap-6">
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:px-10 lg:px-5 xl:px-0">
        <PrismicRichText
          field={slice.primary.tag}
          components={{
            paragraph: ({children}) => <p className="text-button-s font-semibold text-moss-grey-400">{children}</p>,
            strong: ({children}) => <strong className="pr-3 font-semibold text-moss-blue">{children}</strong>,
          }}
        />
        <div className="flex flex-col">
          <PrismicRichText
            field={slice.primary.title}
            components={{
              paragraph: ({children}) => (
                <h2 className="text-tg-h2 font-normal leading-[normal] text-moss-grey-400">{children}</h2>
              ),
              strong: ({children}) => (
                <>
                  <span className="text-tg-h2 font-normal leading-[normal] text-black">{children}</span>
                  <br className="max-lg:hidden" />
                </>
              ),
            }}
          />
        </div>
      </div>
      {/* Grid */}
      <div
        className={cn(
          'relative isolate grid w-full grid-cols-[20px_1fr_1fr_20px] grid-rows-[auto_40px_1fr_40px] md:grid-cols-[minmax(20px,1fr)_minmax(0,min(50%,610px))_minmax(0,min(25%,297px))_minmax(0,min(25%,297px))_minmax(20px,1fr)] md:grid-rows-[64px,auto,80px]',
          GRID_CLASSES[slice.primary.grid_cells?.length || 0],
        )}
      >
        <Gradient color="255, 255, 255" gradientStart={0} height={24} location="top" className="max-md:hidden" />

        {/* First row */}
        <div className="col-span-1 rounded-br-[4px] border-b border-r border-moss-grey-150 max-md:row-start-2" />
        <div className="col-span-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:col-span-1 max-md:row-start-2" />
        <div className="col-span-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:col-span-1 max-md:row-start-2" />
        {slice.primary.grid_cells?.length === 3 && (
          <div className="col-span-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:col-span-1 max-md:row-start-2" />
        )}
        <div className="col-span-1 ml-[-1px] rounded-b-[4px] border-x border-b border-moss-grey-150 max-md:hidden" />
        <div className="col-span-1 ml-[-1px] rounded-bl-[4px] border-b border-l border-moss-grey-150 max-md:row-start-2" />

        {/* Content */}
        <div className="col-span-1 mt-[-1px] rounded-r-[4px] border-y border-r border-moss-grey-150 max-md:row-start-3" />
        <div className="relative col-span-1 ml-[-1px] mt-[-1px] flex flex-col gap-6 rounded-[4px] border border-moss-grey-150 pb-3 max-md:col-span-full max-md:col-start-1 max-md:row-start-1 max-md:border-none max-md:px-5 md:p-4">
          <Gradient
            color="255, 255, 255"
            gradientStart={0}
            height={24}
            location="top"
            className="!top-auto bottom-[-24px] left-0 md:hidden"
          />
          <p className="text-tg-body-m max-w-[292px] text-black/[60%]">{slice.primary.description}</p>
          <Link
            href={linkResolver(primaryButtonData?.link)}
            className="group relative flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] hover:bg-moss-grey-100"
            onClick={() =>
              track({
                event: EVENTS.CLICK,
                mktgId: primaryButtonData?.marketing_id!,
                location: `component-${componentIndex}-security-section`,
                pageName: context.pageUid,
                dataType: 'button',
              })
            }
          >
            <span className="text-button-s uppercase text-black">{primaryButtonData?.label}</span>
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-green-neon group-hover:bg-green-neon/[80%]">
              <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
            </div>
          </Link>
        </div>
        {slice.primary.grid_cells?.map((content, index) => (
          <GridCell
            key={index}
            index={index}
            content={content}
            isFirst={index === 0}
            isLast={index === slice.primary.grid_cells.length - 1}
          />
        ))}

        <div
          className={`max-md:col-start-${
            slice.primary.grid_cells.length + 2
          } col-span-1 ml-[-1px] mt-[-1px] rounded-l-[4px] border-y border-l border-moss-grey-150 max-md:row-start-3`}
        />

        {/* Last Row */}
        <div className="col-span-1 mt-[-1px] rounded-tr-[4px] border-r border-t border-moss-grey-150 max-md:row-start-4" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:row-start-4" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:row-start-4" />
        {slice.primary.grid_cells?.length === 3 && (
          <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:row-start-4" />
        )}
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-t-[4px] border-x border-t border-moss-grey-150 max-md:hidden" />
        <div className="col-span-1 ml-[-1px] mt-[-1px] rounded-tl-[4px] border-l border-t border-moss-grey-150 max-md:row-start-4" />

        <Gradient color="255, 255, 255" gradientStart={0} height={24} location="bottom" />
      </div>
    </section>
  )
}

export default SideGrid
